/**
 * @description
 * Cost reports tab component for displaying Anthropic cost data and analytics.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import type { CostReportsProps } from "./types";

export function CostReports({
  costData,
  costStartingAt,
  costEndingAt,
  costBucketWidth,
  workspaces,
  loading: _loading,
  onSetCostStartingAt,
  onSetCostEndingAt,
  onSetCostBucketWidth,
  onFetchCost,
}: CostReportsProps) {
  const getWorkspaceName = (workspaceId: string | null): string => {
    if (!workspaceId) return "N/A";
    const workspace = workspaces.find((w) => w.anthropicId === workspaceId);
    return workspace ? workspace.name : workspaceId;
  };

  const calculateTotalCost = () => {
    let total = 0;
    costData.forEach((bucket) => {
      bucket.results.forEach((result) => {
        // Amount is in cents, convert to dollars
        total += parseFloat(result.amount) / 100;
      });
    });
    return total;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Cost Reports</h2>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div>
          <label className="text-sm font-medium">Start Time</label>
          <input
            type="datetime-local"
            value={costStartingAt.slice(0, 16)}
            onChange={(e) =>
              onSetCostStartingAt(new Date(e.target.value).toISOString())
            }
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="text-sm font-medium">End Time</label>
          <input
            type="datetime-local"
            value={costEndingAt.slice(0, 16)}
            onChange={(e) =>
              onSetCostEndingAt(new Date(e.target.value).toISOString())
            }
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Bucket Width</label>
          <select
            value={costBucketWidth}
            onChange={(e) => onSetCostBucketWidth(e.target.value as "1d")}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="1d">1 Day</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={onFetchCost}
            className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Fetch Cost
          </button>
        </div>
      </div>

      {costData.length > 0 && (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-md border p-4">
              <p className="text-sm font-medium text-muted-foreground">
                Total Cost
              </p>
              <p className="mt-2 text-2xl font-bold">
                ${calculateTotalCost().toFixed(2)}
              </p>
            </div>
            <div className="rounded-md border p-4">
              <p className="text-sm font-medium text-muted-foreground">
                Average per Day
              </p>
              <p className="mt-2 text-2xl font-bold">
                ${(calculateTotalCost() / costData.length).toFixed(2)}
              </p>
            </div>
            <div className="rounded-md border p-4">
              <p className="text-sm font-medium text-muted-foreground">
                Currency
              </p>
              <p className="mt-2 text-2xl font-bold">
                {costData[0]?.results[0]?.currency || "USD"}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {costData.map((bucket, bucketIndex) => (
              <div key={bucketIndex} className="rounded-md border p-4">
                <h3 className="mb-3 font-medium">
                  {new Date(bucket.starting_at).toLocaleString()} -{" "}
                  {new Date(bucket.ending_at).toLocaleString()}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b">
                      <tr>
                        <th className="pb-2 text-left font-medium">
                          Workspace
                        </th>
                        <th className="pb-2 text-left font-medium">
                          Description
                        </th>
                        <th className="pb-2 text-left font-medium">
                          Type
                        </th>
                        <th className="pb-2 text-right font-medium">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {bucket.results.map((result, resultIndex) => (
                        <tr key={resultIndex}>
                          <td className="py-2">
                            {getWorkspaceName(result.workspace_id)}
                          </td>
                          <td className="py-2 text-xs">
                            {result.description || "N/A"}
                          </td>
                          <td className="py-2 text-xs">
                            {result.cost_type || "N/A"}
                          </td>
                          <td className="py-2 text-right font-medium">
                            ${(parseFloat(result.amount) / 100).toFixed(4)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
